import type { DelegationOwnerType } from './statisticsPlanUtils'

type CumulativeInputRow = {
  id: number
  month: string
  samplesTaken: number
  samplesPlanned: number
}

type CumulativeOutputRow = CumulativeInputRow & {
  date: string
  label: string
  cumulativeTaken: number
  cumulativePlanned: number
}

const getEndOfMonth = (year: number, monthIndex: number) =>
  new Date(Date.UTC(year, monthIndex + 1, 0))

export const getCumulativeReportCutoff = (year: number, now = new Date()) => {
  const today = new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Oslo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  }).format(now)
  return today < `${year}-12-31` ? today : `${year}-12-31`
}

export const getSubsampleResultMetrics = (
  analyzedSubsampleCount: number,
  positiveSubsampleCount: number
) => ({
  analyzedSubsampleCount,
  positiveSubsampleCount,
  positivePercent:
    analyzedSubsampleCount > 0
      ? Math.round((positiveSubsampleCount / analyzedSubsampleCount) * 100)
      : 0
})

export const getOsloYearDateRange = (year: number) => ({
  fromDate: new Date(Date.UTC(year - 1, 11, 31, 23)).toISOString(),
  toDate: new Date(Date.UTC(year, 11, 31, 22, 59, 59, 999)).toISOString()
})

export const isDateInOsloYear = (date: string | undefined, year: number) =>
  date != null &&
  new Intl.DateTimeFormat('sv-SE', {
    timeZone: 'Europe/Oslo',
    year: 'numeric'
  }).format(new Date(date)) === String(year)

export const sortEftaCodes = (values: string[]) =>
  [...values].sort((a, b) =>
    a.localeCompare(b, 'nb-NO', { numeric: true, sensitivity: 'base' })
  )

export const buildCumulativeCalendarSeries = (
  rows: CumulativeInputRow[],
  options?: { year?: number; now?: Date }
) => {
  const year = options?.year ?? new Date().getUTCFullYear()
  const cutoff = getCumulativeReportCutoff(year, options?.now)

  return rows.reduce<CumulativeOutputRow[]>((acc, row, index) => {
    const previous = acc.at(-1)
    const monthEndDate = getEndOfMonth(year, row.id)
    const cumulativePlanned =
      (previous?.cumulativePlanned ?? 0) + row.samplesPlanned
    const monthStart = new Date(Date.UTC(year, row.id, 1))
      .toISOString()
      .slice(0, 10)
    const samplesTaken = monthStart <= cutoff ? row.samplesTaken : 0
    const cumulativeTaken = (previous?.cumulativeTaken ?? 0) + samplesTaken

    acc.push({
      ...row,
      samplesTaken,
      id: index,
      date: monthEndDate.toISOString(),
      label: row.month,
      cumulativeTaken,
      cumulativePlanned
    })

    return acc
  }, [])
}

export const buildStatusAtEndOfPreviousMonth = (
  rows: Array<{
    id: number
    date?: string
    cumulativeTaken: number | null
    cumulativePlanned: number
  }>,
  year: number,
  statusMonthIndex: number
) => {
  const statusDate = new Date(Date.UTC(year, statusMonthIndex + 1, 0))
  const row = rows.findLast((statusRow) => {
    if (!statusRow.date) return statusRow.id <= statusMonthIndex
    return new Date(statusRow.date) <= statusDate
  })
  const expected = row?.cumulativePlanned ?? 0
  const planned = row?.cumulativePlanned ?? 0
  const taken =
    rows
      .filter((statusRow) => {
        if (!statusRow.date) return statusRow.id <= statusMonthIndex
        return new Date(statusRow.date) <= statusDate
      })
      .findLast((statusRow) => statusRow.cumulativeTaken != null)
      ?.cumulativeTaken ?? 0
  const achievementComparedToExpected = taken - expected
  const achievementComparedToPlanned = taken - planned

  return {
    statusDate: statusDate.toISOString(),
    expected,
    planned,
    taken,
    achievementComparedToExpected,
    achievementPercentComparedToExpected:
      expected > 0 ? (taken / expected) * 100 : 0,
    achievementComparedToPlanned,
    achievementPercentComparedToPlanned:
      planned > 0 ? (taken / planned) * 100 : 0
  }
}

export const getOwnerTypeLabel = (ownerType: DelegationOwnerType) => {
  switch (ownerType) {
    case 'team':
      return 'team'
    case 'seksjon':
      return 'seksjon'
    case 'avdeling':
    default:
      return 'avdeling'
  }
}

type SamplesDurationStatusAvdeling = {
  id: string
  name: string
  sampleTaken: number
  sampleSent: number
  sampleReceived: number
  analysisStarted: number
  compromised: number
  readyToProcess: number
  completed: number
  closed: number
  totalSamples: number
}

export function mapArrayToSamplesDurationStatusAvdeling(
  inputArr: {
    externalId: string
    statuses: { avgDays: number; numSamples: number; status: string }[]
    type: string
  }[],
  owners: {
    id: string
    name: string
    type: string
  }[]
): SamplesDurationStatusAvdeling[] {
  return inputArr.map((input) => {
    const ownerName = owners.find(
      (o) => o.id === `${input.type}:${input.externalId}`
    )?.name
    const result: SamplesDurationStatusAvdeling = {
      id: input.externalId,
      name: ownerName ?? `${input.type}:${input.externalId}`,
      sampleTaken: 0,
      sampleSent: 0,
      sampleReceived: 0,
      analysisStarted: 0,
      compromised: 0,
      readyToProcess: 0,
      completed: 0,
      closed: 0,
      totalSamples: 0
    }

    for (const s of input.statuses) {
      result.totalSamples = Math.max(result.totalSamples, s.numSamples)

      if (s.status === 'SampleTaken') {
        result.sampleTaken = s.avgDays
      } else if (s.status === 'SampleSent') {
        result.sampleSent = s.avgDays
      } else if (s.status === 'SampleReceived') {
        result.sampleReceived = s.avgDays
      } else if (s.status === 'AnalysisStarted') {
        result.analysisStarted = s.avgDays
      } else if (s.status === 'Compromised') {
        result.compromised = s.avgDays
      } else if (s.status === 'ReadyToProcess') {
        result.readyToProcess = s.avgDays
      } else if (s.status === 'Completed') {
        result.completed = s.avgDays
      } else if (s.status === 'Closed') {
        result.closed = s.avgDays
      }
    }
    return result
  })
}
